import { SkyDrawer, SkyText } from "@skypointcloud/skypoint-ui";
import { useMemo, useState } from "react";
import { FieldRenderer } from "./FieldRenderer";
import { useFieldDependencies } from "./useFieldDependencies";
import { hasFieldErrors, useValidation } from "./useValidation";
import type { EntityRow } from "./EntityTable";
import type { FieldDefinition, FormValues } from "./types";

/**
 * Naming an entity the validator did not list (FR-44).
 *
 * File-based connectors (SFTP, FTP) have no vendor-discovered table list: their
 * entities are paths a person curates. The form schema for one of them is
 * `entitySelection.addEntityFields` in the connector seed, which is shape-
 * compatible with the top-level `fieldDefinitions`, so this drawer reuses the
 * same renderer, dependency engine and validation rules rather than growing a
 * second, slightly different form.
 *
 * Ported from skypoint-studio's `AddEntityDrawer.tsx`. The drawer's own
 * signature changed with the pinned library (`onOpenChange` takes a boolean in
 * skypoint-ui 2.4.0 and still does in 2.17.0), and the result is mapped onto this product's
 * `EntityRow` rather than Studio's loose `EntityItem` bag.
 */

export interface AddEntityDrawerProps {
  open: boolean;
  onClose: () => void;
  addEntityFields: FieldDefinition[];
  /** Existing names, so the drawer can refuse a duplicate before the server does. */
  existingEntityNames: string[];
  onAdd: (entity: EntityRow) => void;
  entityLabelSingular?: string;
}

function initialValues(fields: FieldDefinition[]): FormValues {
  const values: FormValues = {};
  for (const field of fields) values[field.id] = field.defaultValue ?? "";
  return values;
}

function textOf(values: FormValues, id: string): string {
  const value = values[id];
  return value == null ? "" : String(value).trim();
}

export function AddEntityDrawer({
  open,
  onClose,
  addEntityFields,
  existingEntityNames,
  onAdd,
  entityLabelSingular = "entity",
}: AddEntityDrawerProps) {
  const fields = useMemo(
    () => [...addEntityFields].sort((a, b) => a.order - b.order),
    [addEntityFields],
  );

  const [values, setValues] = useState<FormValues>(() => initialValues(fields));
  const [submitted, setSubmitted] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);

  // Reopening must not inherit the last entity's values; a drawer that
  // remembers is how two entities end up with one path. Adjusted during render
  // rather than in an effect (React's "derive state from props" pattern), so
  // the drawer never paints one frame of the previous entity.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setValues(initialValues(fields));
      setSubmitted(false);
    }
  }

  const fieldState = useFieldDependencies(fields, values);
  const ruleErrors = useValidation(fields, values, fieldState);

  const errors = useMemo(() => {
    const merged = { ...ruleErrors };
    const name = textOf(values, "name");
    // Uniqueness is the one rule the seed cannot express: only this drawer
    // knows the list the entity is joining.
    if (name && existingEntityNames.includes(name)) {
      merged["name"] = `This connection already has an entity called "${name}".`;
    }
    return merged;
  }, [ruleErrors, values, existingEntityNames]);

  const handleAdd = () => {
    setSubmitted(true);
    if (hasFieldErrors(errors)) return;

    const name = textOf(values, "name");
    if (!name) return;

    onAdd({
      name,
      displayName: textOf(values, "displayName") || name,
      sourceSchema: textOf(values, "schema") || null,
      sourceTable: textOf(values, "path") || textOf(values, "table") || null,
      isSelected: true,
      incrementalEnabled: values["incrementalEnabled"] === true,
      watermarkColumn: textOf(values, "watermarkColumn") || null,
      keyColumns: textOf(values, "keyColumns")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean),
    });
    onClose();
  };

  return (
    <SkyDrawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      position="end"
      size="small"
      testId="add-entity-drawer"
      // #2428: "Add entity", never "Add an report": no article to get wrong.
      headerTitle={`Add ${entityLabelSingular.toLowerCase()}`}
      showCloseButton
      footerButtons={[
        { label: "Cancel", onClick: onClose, appearance: "secondary" },
        { label: "Add", onClick: handleAdd, appearance: "primary" },
      ]}
    >
      <div className="flex flex-col gap-4">
        <SkyText as="p" size={200} color="muted" block>
          Name something the source exposes but the last validation did not list: a view created
          after the connection was validated, or a path this connector cannot discover.
        </SkyText>
        {fields.map((field) => {
          const state = fieldState[field.id];
          if (state && !state.visible) return null;
          return (
            <FieldRenderer
              key={field.id}
              field={field}
              testIdPrefix="add-entity-field"
              value={values[field.id]}
              error={submitted ? (errors[field.id] ?? null) : null}
              disabled={state?.disabled ?? field.disabled}
              required={state?.required ?? field.validation.required?.value ?? false}
              onChange={(fieldId, value) =>
                setValues((previous) => ({ ...previous, [fieldId]: value }))
              }
            />
          );
        })}
      </div>
    </SkyDrawer>
  );
}
